import { useState, useId, type FormEvent } from 'react';
import { useOne, useCreate, useUpdate, useList, useGo, useCustomMutation, useInvalidate } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import { Loader2, Globe } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select';
import { FormHeader, FormStickyActions, FormSpacer } from '@/components/forms';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { notify } from '@/lib/notify';
import type { CountryItem } from './types';
import { codeToFlagEmoji } from './types';
import type { TagItem } from '../tags/types';
import { resolveApiUrl } from '@/providers/api-url';

interface CountryFormData {
  code: string;
  name: string;
  dialCode: string;
  isActive: boolean;
  tagIds: number[];
  allowedCurrencies: string[];
}

function buildInitialState(country?: CountryItem): CountryFormData {
  if (!country) {
    return { code: '', name: '', dialCode: '', isActive: true, tagIds: [], allowedCurrencies: [] };
  }
  return {
    code: country.code,
    name: country.name,
    dialCode: country.dialCode ?? '',
    isActive: country.isActive,
    tagIds: country.tagIds,
    allowedCurrencies: country.allowedCurrencies ?? [],
  };
}

export function CountriesForm() {
  const { id } = useParams<{ id: string }>();
  const go = useGo();
  const isEdit = !!id;

  const oneResult = useOne<CountryItem>({
    resource: 'admin/countries',
    id: id ? parseInt(id, 10) : undefined,
    queryOptions: { enabled: isEdit },
  });

  const countryData = oneResult.query.data?.data;
  const isLoadingData = oneResult.query.isLoading;

  if (isEdit && isLoadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const formKey = isEdit ? `edit-${countryData?.id ?? 'loading'}` : 'create';

  return (
    <CountryFormFields
      key={formKey}
      mode="page"
      isEdit={isEdit}
      id={id}
      initialState={buildInitialState(countryData)}
      onBack={() => go({ to: '/settings/countries' })}
      onSuccess={() => go({ to: '/settings/countries' })}
    />
  );
}

interface CountryFormFieldsProps {
  mode: 'page' | 'modal';
  isEdit: boolean;
  id?: string;
  initialState: CountryFormData;
  onBack: () => void;
  onSuccess: () => void;
}

function CountryFormFields({ mode, isEdit, id, initialState, onBack, onSuccess }: CountryFormFieldsProps) {
  const formId = useId();
  const [formData, setFormData] = useState<CountryFormData>(initialState);

  const createMutation = useCreate<CountryItem>();
  const updateMutation = useUpdate<CountryItem>();
  const setTagsMutation = useCustomMutation();

  const isSubmitting =
    createMutation.mutation.isPending
    || updateMutation.mutation.isPending
    || setTagsMutation.mutation.isPending;

  const tagsResult = useList<TagItem>({
    resource: 'tags',
    pagination: { pageSize: 500, mode: 'server' },
  });
  const tags = tagsResult.query.data?.data ?? [];
  const activeTags = tags.filter((t) => t.deletedAt === null);
  const tagOptions: MultiSelectOption[] = activeTags.map((t) => ({
    value: String(t.id),
    label: t.name,
  }));
  const tagsById = new Map<number, TagItem>(activeTags.map((t) => [t.id, t]));

  const currenciesResult = useList<{ id: string; code: string; name: string; symbol: string; isActive: boolean }>({
    resource: 'admin/currencies',
    pagination: { pageSize: 200, mode: 'server' },
  });
  const currencies = (currenciesResult.query.data?.data ?? []).filter((c) => c.isActive);
  const currencyOptions: MultiSelectOption[] = currencies.map((c) => ({
    value: c.code,
    label: `${c.symbol} ${c.code} — ${c.name}`,
  }));

  const handleChange = <K extends keyof CountryFormData>(field: K, value: CountryFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validate = (): boolean => {
    if (!/^[A-Za-z]{2}$/.test(formData.code.trim())) {
      notify.error('Код страны должен быть ровно из 2 латинских букв (ISO 3166-1 alpha-2)');
      return false;
    }
    if (!formData.name.trim()) {
      notify.error('Введите название страны');
      return false;
    }
    if (formData.dialCode.trim() && !/^\+?[0-9]{1,7}$/.test(formData.dialCode.trim())) {
      notify.error('Телефонный код имеет неверный формат (например: +998)');
      return false;
    }
    return true;
  };

  const updateTagsIfChanged = async (countryId: number) => {
    const initialIds = [...initialState.tagIds].sort();
    const currentIds = [...formData.tagIds].sort();
    if (initialIds.length === currentIds.length && initialIds.every((v, i) => v === currentIds[i])) {
      return;
    }
    await setTagsMutation.mutateAsync({
      url: `${resolveApiUrl()}/admin/countries/${countryId}/tags`,
      method: 'put',
      values: { tagIds: formData.tagIds },
    });
  };

  const submitData = async () => {
    if (!validate()) return;

    const baseValues = {
      code: formData.code.trim().toUpperCase(),
      name: formData.name.trim(),
      dialCode: formData.dialCode.trim() ? formData.dialCode.trim() : null,
      isActive: formData.isActive,
      allowedCurrencies: formData.allowedCurrencies.map((c) => c.toUpperCase()),
    };

    try {
      if (isEdit && id) {
        await updateMutation.mutateAsync({
          resource: 'admin/countries',
          id: parseInt(id, 10),
          values: baseValues,
        });
        await updateTagsIfChanged(parseInt(id, 10));
        onSuccess();
      } else {
        await createMutation.mutateAsync({
          resource: 'admin/countries',
          values: { ...baseValues, tagIds: formData.tagIds },
        });
        onSuccess();
      }
    } catch (error) {
      const message = (error instanceof Error ? error.message : null) ?? 'Ошибка сохранения';
      notify.error(message);
    }
  };

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    void submitData();
  };

  const flag = codeToFlagEmoji(formData.code);

  const fields = (
    <form id={formId} onSubmit={handleFormSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Основные данные</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-[80px_1fr] gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${formId}-code`}>Код *</Label>
              <Input
                id={`${formId}-code`}
                value={formData.code}
                onChange={(e) => handleChange('code', e.target.value.toUpperCase().slice(0, 2))}
                placeholder="UZ"
                maxLength={2}
                className="font-mono uppercase"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${formId}-name`}>Название *</Label>
              <Input
                id={`${formId}-name`}
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Узбекистан"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
            <span className="text-3xl">{flag}</span>
            <span className="text-sm text-muted-foreground">
              Флаг вычисляется автоматически из кода
            </span>
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${formId}-dialCode`}>Телефонный код</Label>
            <Input
              id={`${formId}-dialCode`}
              value={formData.dialCode}
              onChange={(e) => handleChange('dialCode', e.target.value)}
              placeholder="+998"
            />
          </div>

          <div className="flex items-center justify-between rounded-md border px-3 py-3">
            <div>
              <Label htmlFor={`${formId}-isActive`} className="cursor-pointer">Активна</Label>
              <p className="text-xs text-muted-foreground">
                Если отключена — не показывается при регистрации и в селекторе профиля
              </p>
            </div>
            <Switch
              id={`${formId}-isActive`}
              checked={formData.isActive}
              onCheckedChange={(checked) => handleChange('isActive', checked)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Авто-теги</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Когда пользователь выбирает эту страну, ему автоматически проставляются все
            отмеченные теги (UserTag.source = country_auto). При смене страны старые
            авто-теги удаляются, новые — добавляются.
          </p>

          <MultiSelect
            options={tagOptions}
            value={formData.tagIds.map(String)}
            onChange={(ids) => handleChange('tagIds', ids.map((v) => parseInt(v, 10)))}
            placeholder="Выберите теги..."
            searchPlaceholder="Поиск тегов..."
            emptyMessage="Теги не найдены"
          />

          {formData.tagIds.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {formData.tagIds.map((id) => {
                const tag = tagsById.get(id);
                if (!tag) return null;
                return (
                  <Badge
                    key={id}
                    variant="outline"
                    style={{ borderColor: tag.color, color: tag.color }}
                  >
                    {tag.name}
                  </Badge>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Разрешённые валюты</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Список валют, доступных пользователям этой страны для выбора в настройках профиля.
            Если ничего не выбрано — доступны все системные валюты.
          </p>

          <MultiSelect
            options={currencyOptions}
            value={formData.allowedCurrencies}
            onChange={(codes) => handleChange('allowedCurrencies', codes)}
            placeholder="Выберите валюты..."
            searchPlaceholder="Поиск валюты..."
            emptyMessage="Валюты не найдены"
          />

          {formData.allowedCurrencies.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {formData.allowedCurrencies.map((code) => (
                <Badge key={code} variant="outline">{code}</Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </form>
  );

  if (mode === 'modal') {
    return (
      <>
        {fields}
        <DialogFooter className="mt-6">
          <Button type="button" variant="outline" onClick={onBack} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button type="button" onClick={() => void submitData()} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <FormHeader
        title={isEdit ? 'Редактировать страну' : 'Добавить страну'}
        description="Привязка тегов и видимость для регистрации"
        sticky
        icon={<Globe className="h-5 w-5" />}
        onBack={onBack}
      />

      {fields}

      <FormSpacer />
      <FormStickyActions
        primaryLabel={isEdit ? 'Сохранить' : 'Создать'}
        secondaryLabel="Отмена"
        onPrimary={() => void submitData()}
        onSecondary={onBack}
        isPending={isSubmitting}
        pendingLabel={isEdit ? 'Сохранение...' : 'Создание...'}
      />
    </div>
  );
}

interface CountryCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CountryCreateDialog({ open, onOpenChange }: CountryCreateDialogProps) {
  const invalidate = useInvalidate();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Добавить страну</DialogTitle>
          <DialogDescription>
            Привязка тегов и видимость для регистрации
          </DialogDescription>
        </DialogHeader>

        <CountryFormFields
          key={open ? 'create-open' : 'create-closed'}
          mode="modal"
          isEdit={false}
          initialState={buildInitialState()}
          onBack={() => onOpenChange(false)}
          onSuccess={() => {
            onOpenChange(false);
            invalidate({ resource: 'admin/countries', invalidates: ['list'] });
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

interface CountryEditDialogProps {
  country: CountryItem | null;
  onClose: () => void;
}

export function CountryEditDialog({ country, onClose }: CountryEditDialogProps) {
  const invalidate = useInvalidate();
  const open = country !== null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Редактировать страну</DialogTitle>
          <DialogDescription>
            Привязка тегов и видимость для регистрации
          </DialogDescription>
        </DialogHeader>

        {country && (
          <CountryFormFields
            key={`edit-${country.id}`}
            mode="modal"
            isEdit
            id={String(country.id)}
            initialState={buildInitialState(country)}
            onBack={onClose}
            onSuccess={() => {
              onClose();
              invalidate({ resource: 'admin/countries', invalidates: ['list', 'detail'] });
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
